import { asyncHandler } from '../async-handler';
import store from '../../../store/store';
import { RequestInfo } from '../../../interfaces/RequestInfo';
import {
  TARA_CUSTOM_INSTRUCTIONS_API,
  TARA_CUSTOM_INSTRUCTIONS_SETTINGS_API,
} from '../../constants/central-api';

export interface TaraCustomInstructionsSettings {
  chat: boolean;
  notifications: boolean;
  reports: boolean;
  custom_prompts: boolean;
  graphs: boolean;
}

export interface TaraCustomInstructionsResponse {
  success: boolean;
  message: string | null;
  data: {
    instructions: string;
    settings: TaraCustomInstructionsSettings;
  };
}

export interface TaraCustomInstructions {
  instructions: string;
  settings: TaraCustomInstructionsSettings;
}

export interface SaveInstructionsRequest {
  instructions: string;
}

export interface UpdateSettingsRequest {
  settings: TaraCustomInstructionsSettings;
}

export default async function get_tara_custom_instructions_api_call(): Promise<TaraCustomInstructions | null> {
  const shopSlug = store.getState().user?.company?.shop_slug;
  if (!shopSlug) {
    console.error('Shop slug is not available');
    return null;
  }

  const accessToken = store.getState().auth.authorization.accessToken;
  const sellerToken = accessToken?.sellerToken;

  const requestInfo: RequestInfo = {
    url: TARA_CUSTOM_INSTRUCTIONS_API(shopSlug),
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: accessToken.type + ' ' + sellerToken,
    },
  };

  const actionType = 'GET_TARA_CUSTOM_INSTRUCTIONS';
  const customMsg = 'Tara Custom Instructions Fetched Successfully.';

  const res: any = await asyncHandler(
    requestInfo,
    actionType,
    customMsg,
    false
  );

  const response: TaraCustomInstructionsResponse = res?.data;
  if (!response?.success) {
    return null;
  }

  if (res.actionType === actionType && response.success === true) {
    return response.data || null;
  }

  return null;
}

export async function save_tara_custom_instructions_api_call(
  instructions: string
): Promise<boolean> {
  const shopSlug = store.getState().user?.company?.shop_slug;
  if (!shopSlug) {
    console.error('Shop slug is not available');
    return false;
  }

  const accessToken = store.getState().auth.authorization.accessToken;
  const sellerToken = accessToken?.sellerToken;

  const requestInfo: RequestInfo = {
    url: TARA_CUSTOM_INSTRUCTIONS_API(shopSlug),
    method: 'POST',
    body: JSON.stringify({ instructions }),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: accessToken.type + ' ' + sellerToken,
    },
  };

  const actionType = 'SAVE_TARA_CUSTOM_INSTRUCTIONS';
  const customMsg = 'Tara Custom Instructions Saved Successfully.';

  const res: any = await asyncHandler(
    requestInfo,
    actionType,
    customMsg,
    true,
    false
  );

  const response: TaraCustomInstructionsResponse = res?.data;
  if (!response?.success) {
    return false;
  }

  if (res.actionType === actionType && response.success === true) {
    return true;
  }

  return false;
}

export async function update_tara_custom_instructions_settings_api_call(
  settings: TaraCustomInstructionsSettings
): Promise<boolean> {
  const shopSlug = store.getState().user?.company?.shop_slug;
  if (!shopSlug) {
    console.error('Shop slug is not available');
    return false;
  }

  const accessToken = store.getState().auth.authorization.accessToken;
  const sellerToken = accessToken?.sellerToken;

  const requestInfo: RequestInfo = {
    url: TARA_CUSTOM_INSTRUCTIONS_SETTINGS_API(shopSlug),
    method: 'PATCH',
    body: JSON.stringify({ settings }),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: accessToken.type + ' ' + sellerToken,
    },
  };

  const actionType = 'UPDATE_TARA_CUSTOM_INSTRUCTIONS_SETTINGS';
  const customMsg = 'Tara Custom Instructions Settings Updated Successfully.';

  const res: any = await asyncHandler(
    requestInfo,
    actionType,
    customMsg,
    true,
    false
  );

  const response: TaraCustomInstructionsResponse = res?.data;
  if (!response?.success) {
    return false;
  }

  if (res.actionType === actionType && response.success === true) {
    return true;
  }

  return false;
}
